import React, { useEffect } from "react";
import { Modal, Form, Input, Select, Button, message, Space, Tag, Tooltip, Row, Col, Typography } from "antd";
import { PlusOutlined, MinusCircleOutlined, InfoCircleOutlined, CalendarOutlined, SearchOutlined, BellOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const MAX_DESCRIPTION_CHARS = 200;
const MAX_USAGE_CHARS = 200;

const sectionCardStyle = {
  background: "#fafafa",
  borderRadius: 12,
  padding: "20px 24px",
  marginBottom: 24,
  border: "1px solid #f0f0f0",
  boxShadow: "0 10px 18px -14px rgba(15, 23, 42, 0.45)",
};

const parameterRowCardStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1.5fr 120px 40px",
  gap: 12,
  marginBottom: 12,
  padding: 12,
  background: "#fff",
  borderRadius: 8,
  border: "1px solid #f0f0f0",
  alignItems: "start",
  boxShadow: "0 8px 14px -12px rgba(15, 23, 42, 0.38)",
};

const CustomToolModal = ({ visible, onCreate, onCancel, loading, mode = "create", initialValues = null }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      if (mode === "edit" && initialValues) {
        const newValues = { ...initialValues };
        if (typeof newValues.headers === "object") {
          newValues.headers = JSON.stringify(newValues.headers, null, 2);
        }
        form.setFieldsValue(newValues);
      } else {
        form.resetFields();
      }
    } else {
      form.resetFields();
    }
  }, [visible, form, mode, initialValues]);

  const webhookParamsRaw = Form.useWatch("webhook_params", form) || [];
  const webhookParams = webhookParamsRaw.filter(Boolean);

  const toolDescription = Form.useWatch("tool_description", form);
  const toolUsageDescription = Form.useWatch("tool_usage_description", form);

  const maxDescriptionLength =
    mode === "edit" && initialValues?.tool_description?.length > MAX_DESCRIPTION_CHARS
      ? initialValues.tool_description.length
      : MAX_DESCRIPTION_CHARS;

  const maxUsageLength =
    mode === "edit" && initialValues?.tool_usage_description?.length > MAX_USAGE_CHARS
      ? initialValues.tool_usage_description.length
      : MAX_USAGE_CHARS;

  const handleOk = () => {
    form.validateFields().then(values => {
      const webhookParams = (values.webhook_params || []).map(p => ({
        field: p.field,
        description: p.description,
        type: p.type,
        required: p.required || false,
      }));
      const requiredFields = webhookParams
        .filter(p => p.required)
        .map(p => p.field);

      let headers = {};
      if (values.headers) {
        try {
          headers = JSON.parse(values.headers);
        } catch (e) {
          message.error("Headers field contains invalid JSON");
          return;
        }
      }

      const payload = {
        ...values,
        headers,
        webhook_params: webhookParams,
        required_fields: requiredFields,
      };
      onCreate(payload);
    }).catch(() => {
      message.error("Please fill in all required fields");
    });
  };

  const toggleParamRequired = (fieldName) => {
    const params = form.getFieldValue("webhook_params") || [];
    const updated = params.map(p => {
      if (p.field === fieldName) {
        return { ...p, required: !p.required };
      }
      return p;
    });
    form.setFieldsValue({ webhook_params: updated });
  };

  const agentTypeOptions = [
    {
      value: "search",
      label: "Search Data",
      description: "Search your org data to find relevant information",
      icon: <SearchOutlined />,
    },
    {
      value: "action",
      label: "Alert Human Team",
      description: "Send alerts or notifications to human team members",
      icon: <BellOutlined />,
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            {mode === "edit" ? "Edit Custom Tool" : "Create Custom Tool"}
          </span>
          <Tag color="blue">Advanced</Tag>
        </Space>
      }
      open={visible}
      onOk={mode === "edit" ? handleOk : handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      width={800}
      okText={mode === "edit" ? "Update Tool" : "Create Tool"}
      cancelText="Cancel"
      destroyOnClose
      style={{ top: 40 }}
      styles={{
        body: { padding: "24px 32px", maxHeight: "75vh", overflowY: "auto", boxShadow: "inset 0 -16px 12px -12px rgba(0,0,0,0.15)" },
        content: {overflow: "hidden" },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          webhook_method: "POST",
          webhook_params: [],
          required_fields: [],
          headers: {},
        }}
        requiredMark="end"
      >
        <div
          style={sectionCardStyle}
        >
          <Space align="center" style={{ marginBottom: 16 }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: "#1f1f1f" }}>Basic Information</span>
            <Tooltip title="Define what this tool does and when it should be used">
              <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
            </Tooltip>
          </Space>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 500 }}>
                    Tool Name <span style={{ color: "#ff4d4f" }}>*</span>
                  </span>
                }
                name="tool_name"
                rules={[
                  { required: true, message: "Please enter tool name" },
                  { max: 15, message: "Max 15 characters allowed" }
                ]}
              >
                <Input
                  placeholder="e.g. check_inventory"
                  style={{ borderRadius: 8 }}
                  maxLength={15}
                  showCount
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 500 }}>
                    Agent Type <span style={{ color: "#ff4d4f" }}>*</span>
                  </span>
                }
                name="agent_type"
                rules={[{ required: true, message: "Please select agent type" }]}
              >
                <Select
                  placeholder="Select agent type"
                  style={{ borderRadius: 8 }}
                  optionLabelProp="label"
                >
                  {agentTypeOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value} label={opt.label}>
                      <Space>
                        <span style={{ fontSize: 18 }}>{opt.icon}</span>
                        <div>
                          <div style={{ fontWeight: 500 }}>{opt.label}</div>
                          <div style={{ fontSize: 12, color: "#8c8c8c" }}>{opt.description}</div>
                        </div>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <span style={{ fontWeight: 500 }}>
                Tool Description <span style={{ color: "#ff4d4f" }}>*</span>
              </span>
            }
            name="tool_description"
            rules={[
              { required: true, message: "Please enter tool description" },
              { max: maxDescriptionLength, message: `Max ${maxDescriptionLength} characters` }
            ]}
          >
            <TextArea
              rows={2}
              placeholder="Describe what this tool does and its purpose..."
              style={{ borderRadius: 8 }}
              maxLength={maxDescriptionLength}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={
              <span style={{ fontWeight: 500 }}>
                Tool Usage Description <span style={{ color: "#ff4d4f" }}>*</span>
              </span>
            }
            name="tool_usage_description"
            rules={[
              { required: true, message: "Please enter usage description" },
              { max: maxUsageLength, message: `Max ${maxUsageLength} characters` }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Describe when and how the AI agent should use this tool..."
              style={{ borderRadius: 8 }}
              maxLength={maxUsageLength}
              showCount
            />
          </Form.Item>
        </div>

        <div
          style={sectionCardStyle}
        >
          <Space align="center" style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: "#1f1f1f" }}>Webhook Configuration</span>
            <Tooltip title="Configure where the tool sends data when triggered">
              <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 13 }}>
            Configure the endpoint and authentication for your tool
          </Text>

          <Row gutter={16}>
            <Col span={18}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 500 }}>
                    Webhook URL <span style={{ color: "#ff4d4f" }}>*</span>
                  </span>
                }
                name="webhook_url"
                rules={[
                  { required: true, message: "Please enter webhook URL" },
                  { type: "url", message: "Please enter a valid URL" },
                ]}
              >
                <Input
                  placeholder="https://your-api.com/webhook/endpoint"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 500 }}>
                    Method <span style={{ color: "#ff4d4f" }}>*</span>
                  </span>
                }
                name="webhook_method"
                rules={[{ required: true, message: "Please select method" }]}
              >
                <Select style={{ borderRadius: 8 }}>
                  <Option value="POST">POST</Option>
                  <Option value="GET">GET</Option>
                  <Option value="PUT">PUT</Option>
                  <Option value="PATCH">PATCH</Option>
                  <Option value="DELETE">DELETE</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 500 }}>
                    Auth Token
                    <Tag color="default" style={{ marginLeft: 8, fontSize: 11 }}>Optional</Tag>
                  </span>
                }
                name="auth_token"
              >
                <Input.Password
                  placeholder="Enter bearer token or API key"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 500 }}>
                    Headers (JSON)
                    <Tag color="default" style={{ marginLeft: 8, fontSize: 11 }}>Optional</Tag>
                  </span>
                }
                name="headers"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      try {
                        JSON.parse(value);
                        return Promise.resolve();
                      } catch (e) {
                        return Promise.reject(new Error("Please enter valid JSON"));
                      }
                    },
                  },
                ]}
              extra={<Text type="secondary" style={{ fontSize: 12 }}>Hint: Use double quotes. <i>{`e.g: {"key":"value"}`}</i></Text>}
              >
                <Input.TextArea
                  rows={4}
                  placeholder='{"Content-Type": "application/json"}'
                  style={{ borderRadius: 8, fontFamily: "monospace", fontSize: 13 }}
                  onBlur={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      form.setFieldsValue({ headers: JSON.stringify(parsed, null, 2) });
                    } catch (error) {
                      // Ignore if JSON is invalid, validator will catch it
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div
          style={sectionCardStyle}
        >
          <Space align="center" style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: "#1f1f1f" }}>Webhook Parameters</span>
            <Tooltip title="Define the data fields to send with each webhook request">
              <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 13 }}>
            Configure parameters that will be sent to your webhook endpoint
          </Text>

          <Form.List name="webhook_params">
            {(fields, { add, remove }) => (
              <>
                {fields.length > 0 && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1.5fr 120px 40px",
                      gap: 12,
                      marginBottom: 8,
                      padding: "8px 12px",
                      background: "#f5f5f5",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#8c8c8c",
                      textTransform: "uppercase",
                    }}
                  >
                    <div>Field Name</div>
                    <div>Description & Example</div>
                    <div>Type</div>
                    <div></div>
                  </div>
                )}

                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={parameterRowCardStyle}
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "field"]}
                      rules={[{ required: true, message: "Field required" }]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        placeholder="e.g. category, name, price"
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "description"]}
                      rules={[{ required: true, message: "Description required" }]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        placeholder="e.g. product category (electronics, clothing)"
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "type"]}
                      rules={[{ required: true, message: "Type required" }]}
                      style={{ margin: 0 }}
                    >
                      <Select placeholder="Type" style={{ borderRadius: 6 }}>
                        <Option value="string">
                          <Space>
                            String
                          </Space>
                        </Option>
                        <Option value="integer">Integer</Option>
                        <Option value="float">Float</Option>
                        <Option value="boolean">Boolean</Option>
                        <Option value="object">Object</Option>
                        <Option value="array">Array</Option>
                      </Select>
                    </Form.Item>

                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(name)}
                      style={{ marginTop: 4 }}
                    />
                  </div>
                ))}

                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                  style={{ borderRadius: 8, height: 44 }}
                >
                  Add Webhook Parameter
                </Button>
              </>
            )}
          </Form.List>
        </div>

        <div
          style={sectionCardStyle}
        >
          <Space align="center" style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: "#1f1f1f" }}>Required Fields</span>
            <Tooltip title="Mark which webhook parameters are required when the tool is called">
              <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 13 }}>
            Click a parameter to toggle it as required (green = required, gray = optional)
          </Text>

          <Row gutter={[12, 12]}>
            {webhookParams.filter(Boolean).map((param) => {
              const isRequired = param.required === true;
              return (
                <Col key={param.field}>
                  <Button
                    onClick={() => toggleParamRequired(param.field)}
                    style={{
                      background: isRequired ? "#f6ffed" : "#f5f5f5",
                      borderColor: isRequired ? "#b7eb8f" : "#d9d9d9",
                      color: isRequired ? "#389e0d" : "#8c8c8c",
                      borderRadius: 8,
                      height: 36,
                      fontWeight: 500,
                      fontSize: 13,
                      paddingLeft: 16,
                      paddingRight: 16,
                    }}
                  >
                    {param.field}
                    {isRequired && (
                      <span style={{ marginLeft: 6, fontSize: 11 }}>✓</span>
                    )}
                  </Button>
                </Col>
              );
            })}
          </Row>
        </div>
      </Form>
    </Modal>
  );
};

export default CustomToolModal;